import { TENANT_ADMIN_WORKFLOWS } from "./workflowCatalog";
import { TENANT_ADMIN_MODULES } from "./moduleCatalog";
import { TENANT_ADMIN_INTEGRATIONS } from "./integrationCatalog";
import {
  resolveWorkflowStatus,
  resolveModuleStatus,
  resolveIntegrationStatus,
} from "./tenantAdminStatus";
import { TENANT_GOVERNANCE_PERMISSION_GROUPS } from "../../constants/tenantGovernancePermissions";

export const TENANT_HEALTH_STATUS_TYPES = {
  healthy: {
    label: "Gezond",
    badgeClass: "bg-emerald-100 text-emerald-800 border-emerald-200",
    dotClass: "bg-emerald-500",
  },
  warning: {
    label: "Waarschuwing",
    badgeClass: "bg-amber-100 text-amber-800 border-amber-200",
    dotClass: "bg-amber-500",
  },
  incomplete: {
    label: "Onvolledig",
    badgeClass: "bg-slate-200 text-slate-800 border-slate-300",
    dotClass: "bg-slate-500",
  },
  inactive: {
    label: "Inactief",
    badgeClass: "bg-zinc-200 text-zinc-700 border-zinc-300",
    dotClass: "bg-zinc-500",
  },
  error: {
    label: "Fout",
    badgeClass: "bg-rose-100 text-rose-800 border-rose-200",
    dotClass: "bg-rose-500",
  },
};

export const TENANT_ONBOARDING_STAGE_TYPES = {
  inrichting_nodig: {
    label: "Inrichting nodig",
    badgeClass: "bg-rose-100 text-rose-800 border-rose-200",
    progressClass: "bg-rose-500",
  },
  bijna_klaar: {
    label: "Bijna klaar",
    badgeClass: "bg-amber-100 text-amber-800 border-amber-200",
    progressClass: "bg-amber-500",
  },
  operationeel: {
    label: "Operationeel",
    badgeClass: "bg-emerald-100 text-emerald-800 border-emerald-200",
    progressClass: "bg-emerald-500",
  },
};

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function toDateOrNull(value) {
  const date = new Date(value || "");
  return Number.isNaN(date.getTime()) ? null : date;
}

function isWithinDays(value, days) {
  const date = toDateOrNull(value);
  if (!date) return false;
  const since = new Date();
  since.setDate(since.getDate() - Number(days || 0));
  return date >= since;
}

function latestDate(values = []) {
  let best = null;
  for (const value of values) {
    const date = toDateOrNull(value);
    if (!date) continue;
    if (!best || date > best) best = date;
  }
  return best;
}

function hasPlanning(workorder) {
  return Boolean(
    workorder?.plannedDate ||
      workorder?.plannedStartAt ||
      workorder?.plannedStartTime ||
      workorder?.plannedEndAt
  );
}

function hasHours(workorder) {
  const hourEntries = asArray(workorder?.hourEntries);
  const autoAdjustments = asArray(workorder?.autoAdjustments);
  const legacyHours = asArray(workorder?.uren);
  const dailyLog = asArray(workorder?.dailyLog);

  const entryHours = hourEntries.reduce((sum, item) => sum + Number(item?.hours || 0), 0);
  const adjustmentHours = autoAdjustments.reduce(
    (sum, item) => sum + Number(item?.hours || 0),
    0
  );
  const legacyTotal = legacyHours.reduce((sum, item) => sum + Number(item?.uren || 0), 0);
  const daylogTotal = dailyLog.reduce((sum, item) => sum + Number(item?.uren || 0), 0);

  return entryHours + adjustmentHours + legacyTotal + daylogTotal > 0;
}

function countByStatus(list = [], statuses = []) {
  const set = new Set(statuses.map((value) => String(value || "").trim().toLowerCase()));
  return list.filter((item) => set.has(String(item?.status || "").trim().toLowerCase())).length;
}

function determineTopStatus(items = []) {
  const order = ["error", "warning", "incomplete", "inactive", "healthy"];
  for (const candidate of order) {
    if (items.some((item) => item.status === candidate)) return candidate;
  }
  return "inactive";
}

function yesNo(value) {
  return value ? "Ja" : "Nee";
}

function hasRoleAssignment(user) {
  if (user?.rol || user?.role) return true;
  const roles = asArray(user?.roles);
  return roles.some((item) => {
    if (typeof item === "string") return item.trim().length > 0;
    return Boolean(item?.name || item?.naam || item?._id);
  });
}

const SETTINGS_ROUTE_PERMISSIONS = TENANT_GOVERNANCE_PERMISSION_GROUPS.settingsView;
const USERS_ROUTE_PERMISSIONS = TENANT_GOVERNANCE_PERMISSION_GROUPS.usersManage;
const ROLES_ROUTE_PERMISSIONS = TENANT_GOVERNANCE_PERMISSION_GROUPS.rolesManage;
const MODULES_ROUTE_PERMISSIONS = TENANT_GOVERNANCE_PERMISSION_GROUPS.modulesView;
const INTEGRATIONS_ROUTE_PERMISSIONS = TENANT_GOVERNANCE_PERMISSION_GROUPS.integrationsView;
const WORKFLOW_ROUTE_PERMISSIONS = TENANT_GOVERNANCE_PERMISSION_GROUPS.workflowsView;

export function resolveOnboardingStage(readinessScore) {
  const score = Math.max(0, Math.min(100, Number(readinessScore || 0)));
  if (score <= 40) return "inrichting_nodig";
  if (score <= 75) return "bijna_klaar";
  return "operationeel";
}

export function buildTenantHealthReport(input = {}) {
  const settings = input?.settings || null;
  const users = asArray(input?.users);
  const workorders = asArray(input?.workorders);
  const photos = asArray(input?.photos?.items);
  const supportTickets = asArray(input?.supportTickets?.items);
  const apiErrors = asArray(input?.apiErrors);
  const mailErrors = asArray(input?.mailErrors);
  const warnings = asArray(input?.warnings);

  const companyFieldsFilled = Boolean(
    settings?.eInvoicing?.sellerLegalName ||
      settings?.eInvoicing?.sellerKvK ||
      settings?.eInvoicing?.sellerVatId
  );
  const brandingConfigured = Boolean(
    settings?.branding?.companyName ||
      settings?.branding?.logoUrl ||
      settings?.branding?.primaryColor
  );
  const mailConfigured = Boolean(
    settings?.integrations?.mail?.smtpHost ||
      settings?.integrations?.mail?.fromAddress ||
      settings?.integrations?.mail?.provider
  );

  const configuredModules = TENANT_ADMIN_MODULES.map((item) =>
    resolveModuleStatus(item.key, settings)
  );
  const configuredWorkflows = TENANT_ADMIN_WORKFLOWS.map((item) =>
    resolveWorkflowStatus(item.key, settings)
  );
  const configuredIntegrations = TENANT_ADMIN_INTEGRATIONS.map((item) =>
    resolveIntegrationStatus(item.key, settings)
  );

  const moduleActiveCount = configuredModules.filter((status) => status === "actief").length;
  const workflowActiveCount = configuredWorkflows.filter((status) => status === "actief").length;
  const integrationConnectedCount = configuredIntegrations.filter(
    (status) => status === "actief"
  ).length;

  const usersActive7d = users.filter((user) => isWithinDays(user?.lastLoginAt, 7)).length;
  const usersWithRoleCount = users.filter((user) => hasRoleAssignment(user)).length;
  const lastLoginAt = latestDate(users.map((user) => user?.lastLoginAt));
  const lastActivityAt = latestDate([
    ...workorders.map((workorder) => workorder?.updatedAt || workorder?.createdAt),
    ...photos.map((item) => item?.createdAt),
  ]);

  const recentWorkorders7d = workorders.filter((workorder) =>
    isWithinDays(workorder?.updatedAt || workorder?.createdAt, 7)
  ).length;
  const recentUploads7d = photos.filter((item) => isWithinDays(item?.createdAt, 7)).length;
  const recentHours7d = workorders.filter((workorder) => {
    const recentlyUpdated = isWithinDays(workorder?.updatedAt || workorder?.createdAt, 7);
    return recentlyUpdated && hasHours(workorder);
  }).length;

  const workordersWithoutPlanning = workorders.filter((workorder) => !hasPlanning(workorder)).length;
  const workordersWithoutHours = workorders.filter((workorder) => !hasHours(workorder)).length;
  const openSupportTickets = countByStatus(supportTickets, [
    "nieuw",
    "open",
    "in_behandeling",
    "wacht_op_klant",
  ]);

  const conceptWorkflows = configuredWorkflows.filter((status) => status === "concept").length;
  const incompleteModules = configuredModules.filter(
    (status) => status === "concept" || status === "niet_ingesteld"
  ).length;
  const integrationWarnings = configuredIntegrations.filter(
    (status) => status === "concept" || status === "niet_ingesteld"
  ).length;

  const workorderSyncRows = workorders.flatMap((workorder) => {
    const moneybird = workorder?.moneybirdIntegrationStatus || {};
    const tu = workorder?.tuIntegrationStatus || {};
    return [
      {
        type: "moneybird",
        status: String(moneybird?.status || "").trim().toLowerCase(),
        lastSyncAt: moneybird?.lastSyncAt || null,
        lastError: String(moneybird?.lastError || "").trim(),
      },
      {
        type: "oci",
        status: String(tu?.status || "").trim().toLowerCase(),
        lastSyncAt: tu?.lastSyncAt || null,
        lastError: String(tu?.lastError || "").trim(),
      },
    ];
  });

  const moneybirdLastSyncAt = latestDate(
    workorderSyncRows.filter((item) => item.type === "moneybird").map((item) => item.lastSyncAt)
  );
  const ociLastSyncAt = latestDate(
    workorderSyncRows.filter((item) => item.type === "oci").map((item) => item.lastSyncAt)
  );
  const moneybirdLastError = workorderSyncRows.find(
    (item) => item.type === "moneybird" && item.lastError
  )?.lastError;
  const ociLastError = workorderSyncRows.find(
    (item) => item.type === "oci" && item.lastError
  )?.lastError;

  const configSection = [
    {
      key: "branding",
      label: "Branding compleet",
      value: yesNo(brandingConfigured),
      status: brandingConfigured ? "healthy" : "incomplete",
      note: brandingConfigured
        ? "Basis branding is ingevuld."
        : "Logo/bedrijfsprofiel nog niet volledig ingevuld.",
      actionLabel: "Branding instellen",
      actionTo: "/tenant-admin/integrations/branding",
      actionPermissionsAny: INTEGRATIONS_ROUTE_PERMISSIONS,
    },
    {
      key: "company_data",
      label: "Bedrijfsgegevens ingevuld",
      value: yesNo(companyFieldsFilled),
      status: companyFieldsFilled ? "healthy" : "warning",
      note: companyFieldsFilled
        ? "Bedrijfsgegevens beschikbaar."
        : "Controleer bedrijfsgegevens voor documenten/facturatie.",
      actionLabel: "Naar instellingen",
      actionTo: "/tenant-admin/settings",
      actionPermissionsAny: SETTINGS_ROUTE_PERMISSIONS,
    },
    {
      key: "roles_setup",
      label: "Rollen ingericht",
      value: `${usersWithRoleCount}/${users.length} gebruikers met rol`,
      status:
        users.length === 0
          ? "incomplete"
          : usersWithRoleCount === users.length
            ? "healthy"
            : usersWithRoleCount > 0
              ? "warning"
              : "incomplete",
      note:
        users.length === 0
          ? "Maak eerst een gebruiker aan voordat roltoewijzing kan gebeuren."
          : usersWithRoleCount === users.length
            ? "Roltoewijzing is aanwezig."
            : "Niet alle gebruikers hebben een duidelijke roltoewijzing.",
      actionLabel: "Rollen beheren",
      actionTo: "/tenant-admin/roles",
      actionPermissionsAny: ROLES_ROUTE_PERMISSIONS,
    },
    {
      key: "modules",
      label: "Modules geconfigureerd",
      value: `${moduleActiveCount}/${TENANT_ADMIN_MODULES.length} actief`,
      status: moduleActiveCount >= 2 ? "healthy" : moduleActiveCount > 0 ? "warning" : "incomplete",
      note: `${incompleteModules} module(s) nog niet volledig ingericht.`,
      actionLabel: "Modules instellen",
      actionTo: "/tenant-admin/modules",
      actionPermissionsAny: MODULES_ROUTE_PERMISSIONS,
    },
    {
      key: "workflows",
      label: "Werkprocessen actief",
      value: `${workflowActiveCount}/${TENANT_ADMIN_WORKFLOWS.length} actief`,
      status: conceptWorkflows === 0 ? "healthy" : "warning",
      note: `${conceptWorkflows} werkproces(sen) staan nog op concept.`,
      actionLabel: "Werkprocessen openen",
      actionTo: "/tenant-admin/workflows",
      actionPermissionsAny: WORKFLOW_ROUTE_PERMISSIONS,
    },
    {
      key: "customer_portal_setup",
      label: "Klantportaal ingericht",
      value:
        resolveWorkflowStatus("workflow_customer_portal", settings) === "actief"
          ? "Actief"
          : "Nog niet ingericht",
      status:
        resolveWorkflowStatus("workflow_customer_portal", settings) === "actief"
          ? "healthy"
          : "incomplete",
      note: "Beheer publicatie- en zichtbaarheidregels voor klantportaalprocessen.",
      actionLabel: "Klantportaal instellen",
      actionTo: "/tenant-admin/workflows/customer-portal",
      actionPermissionsAny: WORKFLOW_ROUTE_PERMISSIONS,
    },
    {
      key: "integrations",
      label: "Integraties gekoppeld",
      value: `${integrationConnectedCount}/${TENANT_ADMIN_INTEGRATIONS.length} actief`,
      status:
        integrationConnectedCount >= 2 ? "healthy" : integrationConnectedCount > 0 ? "warning" : "inactive",
      note: `${integrationWarnings} integratie(s) vragen nog inrichting.`,
      actionLabel: "Integraties openen",
      actionTo: "/tenant-admin/integrations",
      actionPermissionsAny: INTEGRATIONS_ROUTE_PERMISSIONS,
    },
    {
      key: "mail",
      label: "Mailinstellingen aanwezig",
      value: yesNo(mailConfigured),
      status: mailConfigured ? "healthy" : "warning",
      note: mailConfigured ? "Mailbasis is beschikbaar." : "Mailinstellingen ontbreken of zijn onvolledig.",
      actionLabel: "Mail instellen",
      actionTo: "/tenant-admin/integrations/mail",
      actionPermissionsAny: INTEGRATIONS_ROUTE_PERMISSIONS,
    },
  ];

  const activitySection = [
    {
      key: "last_login",
      label: "Laatste login",
      value: lastLoginAt ? lastLoginAt.toISOString() : "-",
      status: lastLoginAt ? "healthy" : "inactive",
      note: lastLoginAt ? "Recente loginactiviteit gevonden." : "Nog geen logininformatie gevonden.",
    },
    {
      key: "active_users_7d",
      label: "Actieve gebruikers (7 dagen)",
      value: `${usersActive7d}/${users.length}`,
      status: users.length === 0 ? "incomplete" : usersActive7d > 0 ? "healthy" : "inactive",
      note:
        users.length === 0
          ? "Nog geen gebruikers gevonden."
          : usersActive7d > 0
            ? "Gebruikers zijn actief."
            : "Geen actieve gebruikers in de laatste 7 dagen.",
      actionLabel: "Gebruikers beheren",
      actionTo: "/tenant-admin/users",
      actionPermissionsAny: USERS_ROUTE_PERMISSIONS,
    },
    {
      key: "workorder_activity",
      label: "Recente werkbonactiviteit",
      value: `${recentWorkorders7d}`,
      status: recentWorkorders7d > 0 ? "healthy" : "inactive",
      note: "Aantal werkbonnen met recente wijziging in 7 dagen.",
    },
    {
      key: "uploads_activity",
      label: "Recente uploads/foto's",
      value: `${recentUploads7d}`,
      status: recentUploads7d > 0 ? "healthy" : "inactive",
      note: "Foto-uploads in de laatste 7 dagen.",
    },
    {
      key: "hours_activity",
      label: "Recente urenregistratie",
      value: `${recentHours7d}`,
      status: recentHours7d > 0 ? "healthy" : "warning",
      note: "Werkbonnen met recente urenindicatie in de laatste 7 dagen.",
    },
  ];

  const integrationSection = [
    {
      key: "moneybird_status",
      label: "Moneybird status",
      value: settings?.integrations?.moneybird?.connected ? "Verbonden" : "Niet verbonden",
      status: settings?.integrations?.moneybird?.connected
        ? "healthy"
        : settings?.integrations?.moneybird?.enabled
          ? "warning"
          : "inactive",
      note: moneybirdLastError || "Geen recente Moneybird-fout bekend.",
      actionLabel: "Moneybird instellen",
      actionTo: "/tenant-admin/integrations/moneybird",
      actionPermissionsAny: INTEGRATIONS_ROUTE_PERMISSIONS,
    },
    {
      key: "mail_status",
      label: "Mailstatus",
      value: mailConfigured ? "Geconfigureerd" : "Niet geconfigureerd",
      status: mailConfigured ? "healthy" : "warning",
      note:
        mailErrors.length > 0
          ? `${mailErrors.length} recente mailfout(en) gedetecteerd.`
          : "Geen recente mailfouten in auditselectie.",
      actionLabel: "Mailinstellingen openen",
      actionTo: "/tenant-admin/integrations/mail",
      actionPermissionsAny: INTEGRATIONS_ROUTE_PERMISSIONS,
    },
    {
      key: "oci_status",
      label: "OCI status",
      value: settings?.integrations?.tu?.connected ? "Verbonden" : "Niet verbonden",
      status: settings?.integrations?.tu?.connected
        ? "healthy"
        : settings?.integrations?.tu?.enabled
          ? "warning"
          : "inactive",
      note: ociLastError || "Geen recente OCI-fout bekend.",
    },
    {
      key: "last_sync",
      label: "Laatste sync moment",
      value: latestDate([moneybirdLastSyncAt, ociLastSyncAt])?.toISOString() || "-",
      status: latestDate([moneybirdLastSyncAt, ociLastSyncAt]) ? "healthy" : "inactive",
      note: "Afgeleid uit recente werkbon-integratiestatussen.",
    },
    {
      key: "last_sync_error",
      label: "Laatste sync fout",
      value: moneybirdLastError || ociLastError || "Geen",
      status: moneybirdLastError || ociLastError ? "error" : "healthy",
      note: "Laatste bekende foutmelding uit werkbon-integratiestatus.",
    },
  ];

  const processSection = [
    {
      key: "without_planning",
      label: "Werkbonnen zonder planning",
      value: `${workordersWithoutPlanning}`,
      status: workordersWithoutPlanning > 0 ? "warning" : "healthy",
      note: "Controleer planningstoewijzing.",
    },
    {
      key: "without_hours",
      label: "Werkbonnen zonder uren",
      value: `${workordersWithoutHours}`,
      status: workordersWithoutHours > 0 ? "warning" : "healthy",
      note: "Kan duiden op incomplete uitvoering.",
    },
    {
      key: "open_support",
      label: "Open supporttickets",
      value: `${openSupportTickets}`,
      status: openSupportTickets > 0 ? "warning" : "healthy",
      note: "Openstaande tickets binnen tenant.",
    },
    {
      key: "error_states",
      label: "Foutstatussen",
      value: `${apiErrors.length}`,
      status: apiErrors.length > 0 ? "error" : "healthy",
      note: "Recente API-fouten binnen tenant-auditselectie.",
    },
    {
      key: "concept_workflows",
      label: "Concept workflows",
      value: `${conceptWorkflows}`,
      status: conceptWorkflows > 0 ? "warning" : "healthy",
      note: "Werkprocessen die nog niet volledig actief zijn.",
    },
    {
      key: "incomplete_modules",
      label: "Niet ingerichte modules",
      value: `${incompleteModules}`,
      status: incompleteModules > 0 ? "incomplete" : "healthy",
      note: "Modules met concept/niet-ingesteld status.",
    },
  ];

  const systemSection = [
    {
      key: "api_errors",
      label: "Laatste API fouten",
      value: `${apiErrors.length}`,
      status: apiErrors.length > 0 ? "error" : "healthy",
      note: "Beperkt overzicht uit auditlogs (laatste selectie).",
    },
    {
      key: "mail_errors",
      label: "Laatste mailfouten",
      value: `${mailErrors.length}`,
      status: mailErrors.length > 0 ? "warning" : "healthy",
      note: "Afgeleid uit auditfouten met mail-signalen.",
    },
    {
      key: "warnings",
      label: "Waarschuwingen",
      value: `${warnings.length}`,
      status: warnings.length > 0 ? "warning" : "healthy",
      note: "Recente warning-severity auditlogs.",
    },
    {
      key: "incomplete_config",
      label: "Incomplete configuratie-items",
      value: `${configSection.filter((item) => item.status !== "healthy").length}`,
      status:
        configSection.filter((item) => item.status !== "healthy").length > 0
          ? "warning"
          : "healthy",
      note: "Items die nog aandacht nodig hebben.",
    },
  ];

  const onboardingChecks = [
    brandingConfigured,
    companyFieldsFilled,
    moduleActiveCount > 0,
    workflowActiveCount >= Math.ceil(TENANT_ADMIN_WORKFLOWS.length / 2),
    integrationConnectedCount > 0,
    mailConfigured,
  ];
  const readinessScore = Math.round(
    (onboardingChecks.filter(Boolean).length / onboardingChecks.length) * 100
  );

  const allItems = [
    ...configSection,
    ...activitySection,
    ...integrationSection,
    ...processSection,
    ...systemSection,
  ];

  return {
    readinessScore,
    tenantReadiness:
      readinessScore >= 85
        ? "healthy"
        : readinessScore >= 60
          ? "warning"
          : readinessScore > 0
            ? "incomplete"
            : "inactive",
    onboardingProgress: `${onboardingChecks.filter(Boolean).length}/${onboardingChecks.length}`,
    lastActivityAt: lastActivityAt ? lastActivityAt.toISOString() : null,
    topStatus: determineTopStatus(allItems),
    sections: {
      configuration: configSection,
      activity: activitySection,
      integrations: integrationSection,
      process: processSection,
      system: systemSection,
    },
  };
}
